import React, { useState, useEffect, useMemo, type ReactNode } from "react";
import { GET_TICKET_ENDPOINT_KEYS } from "../TicketingAPI";
import { useQuery } from "@apollo/client";
import {
  Controller,
  type UseFormReturn,
  type ControllerRenderProps,
  type ControllerFieldState,
  type FieldValues,
  type FieldError,
} from "react-hook-form";
import GenericField, {
  fieldTypeOptions,
  type ServerFieldOption,
} from "../GenericField/GenericField";
import useSections, {
  type GetFieldPropsByFieldName,
  type NormalizedField,
  type RawSection,
} from "../useSections/useSections";
import { SkeletonLoader, REQUIRED_ERROR_MESSAGE } from "@SeemplicityDev/shared";
import "./TicketEndPointKeys.scss";

// Field shape produced by useSections (still .js / untyped). Typed locally from
// runtime usage in this file; widen here when useSections is converted.
interface EndpointKeyField {
  name?: string;
  input?: string;
  label?: string;
  options?: unknown;
  query?: unknown;
  required?: boolean;
  creatable?: boolean;
  isSkeleton?: boolean;
  precedents?: string[];
  descendants?: string[];
  defaultValue?: unknown;
}

type KeysObject = Record<string, unknown>;

// Shape consumed from the GET_TICKET_ENDPOINT_KEYS query. fields_sections is
// passed straight into useSections (untyped), so it stays loosely typed.
interface EndpointKeysQueryData {
  endpoint_keys?: {
    is_final?: boolean;
    has_mapping?: boolean;
    fields_sections?: unknown;
  };
}

export interface TicketEndpointKeysProps {
  disabled?: boolean;
  onFinalPhase?: (isFinal?: boolean, hasMapping?: boolean) => void;
  onKeyChange?: () => void;
  // `useSections` calls this with `() => GetFieldPropsByFieldName`; the consumer
  // wires it to a `useState` setter (lazy-updater form). Typed to match the
  // value useSections supplies.
  onGetProps?: (getProps: () => GetFieldPropsByFieldName) => void;
  onError?: (err: { message?: string } | null) => void;
  ticketProviderId?: string;
  fieldMappingId?: string;
  keysObject: KeysObject;
  formMethods: UseFormReturn<FieldValues>;
  externalEndpointId?: string;
  shouldUnregister?: boolean;
  viewMode?: boolean;
}

const TicketEndpointKeys: React.FC<TicketEndpointKeysProps> = ({
  disabled = false,
  onFinalPhase = () => {},
  onKeyChange = () => {},
  onGetProps = () => {},
  onError = () => {},
  ticketProviderId,
  fieldMappingId,
  keysObject,
  formMethods,
  externalEndpointId,
  shouldUnregister = false,
  viewMode = false,
}) => {
  const [fieldsSections, setFieldsSections] = useState<unknown>(null);
  const {
    control,
    setValue,
    resetField,
    formState: { errors },
  } = formMethods;

  const getField = (field: EndpointKeyField): ReactNode => {
    return field?.isSkeleton &&
      !!filteredKeys &&
      Object.keys(filteredKeys).some((key) =>
        field?.precedents?.includes(key)
      ) ? (
      <div className={"endpoint-keys-loader endpoint-keys-loader-second"}>
        <SkeletonLoader
          className={"endpoint-keys-loader section-row"}
          height={"20px"}
          width={"86px"}
        />
        <SkeletonLoader
          className={"endpoint-keys-loader section-row"}
          height={"26px"}
          width={"100%"}
        />
      </div>
    ) : // Remove next condition to show both keys from the beginning without using skeleton
    field?.isSkeleton ? (
      <></>
    ) : (
      <Controller
        shouldUnregister={shouldUnregister}
        name={`keys.${field?.name}`}
        render={({
          field: { onChange, onBlur, value },
          fieldState: { isTouched },
        }: {
          field: ControllerRenderProps<FieldValues, string>;
          fieldState: ControllerFieldState;
        }) => {
          const fieldLoading =
            ticketEndpointFieldsLoading &&
            !Object.keys(filteredKeys)?.includes(field?.name ?? "");
          return (
            <GenericField
              {...formMethods}
              onChange={(val: unknown) => {
                onChange(val);
                // TEXT fields are excluded from filteredKeys, so changing them
                // does not alter the query variables — Apollo returns a cache hit
                // with no new is_final signal. Calling onKeyChange/onFinalPhase
                // here would permanently disable Next Step because nothing
                // downstream restores it. TEXT keys also never affect descendants
                // or ticket fields, so all cascade side-effects are skipped.
                if (field?.input !== fieldTypeOptions.TEXT) {
                  onKeyChange();
                  if (!!field?.descendants) {
                    resetDescendants(field?.descendants);
                  }
                  onFinalPhase(false, false);
                  resetField("fields", { defaultValue: {} });
                }
              }}
              name={field?.name}
              clearable={false}
              creatable={field?.creatable}
              onBlur={onBlur}
              value={value}
              setSingleOption={false}
              error={
                isTouched
                  ? (errors?.keys as Record<string, FieldError> | undefined)?.[
                      field?.name ?? ""
                    ]
                  : null
              }
              loading={fieldLoading}
              disabled={disabled || fieldLoading}
              key={field?.name}
              input={field?.input}
              label={field?.label}
              options={field?.options as ServerFieldOption[] | undefined}
              query={field?.query as { params?: unknown } | undefined}
              placeholder={`${
                field.input === fieldTypeOptions.TEXT ? "Enter " : "Select "
              }${field.label}`}
              required={field?.required}
              hideLabelHelper={false}
              viewMode={viewMode}
            />
          );
        }}
        rules={{
          ...(field?.required && { required: REQUIRED_ERROR_MESSAGE }),
        }}
        defaultValue={field?.defaultValue}
        control={control}
      />
    );
  };

  const { getSections, getFlatSections, getFieldPropsByFieldName } = useSections(
    // `fieldsSections` is held as `unknown` state and forwarded raw into the hook.
    fieldsSections as RawSection[] | null | undefined,
    onGetProps,
    // `getField` reads a subset of NormalizedField via the local EndpointKeyField
    // view; the runtime field is the normalized object useSections produces.
    getField as (field: NormalizedField) => ReactNode
  );

  // We filter text type out since we don't rely on text input for following keys.
  // Memoised so that typing in a TEXT field doesn't produce a new object reference
  // and trigger an unnecessary Apollo re-evaluation.
  // getFieldPropsByFieldName is included so filteredKeys recomputes when sections
  // load (it changes reference after the first query response).
  const filteredKeys = useMemo(
    () =>
      Object.keys(keysObject)
        .filter((key) => {
          const fieldProps = getFieldPropsByFieldName?.(key)?.input;
          return !!fieldProps && fieldProps !== fieldTypeOptions.TEXT;
        })
        .reduce<KeysObject>(
          (obj, key) => ({ ...obj, [key]: keysObject?.[key] }),
          {}
        ),
    [JSON.stringify(keysObject), getFieldPropsByFieldName] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const { data: endpointKeysData, loading: ticketEndpointFieldsLoading } =
    useQuery<EndpointKeysQueryData>(GET_TICKET_ENDPOINT_KEYS, {
      variables: {
        ticket_provider_id: ticketProviderId,
        selected_keys: !fieldMappingId
          ? JSON.stringify(filteredKeys)
          : undefined,
        field_mapping_id: fieldMappingId,
        partner_external_id: externalEndpointId,
      },
      onError: (err) => {
        onFinalPhase(false, false);
        onError(err);
      },
    });

  useEffect(() => {
    if (!!endpointKeysData) {
      setFieldsSections(endpointKeysData?.endpoint_keys?.fields_sections);
      onFinalPhase(
        endpointKeysData?.endpoint_keys?.is_final,
        endpointKeysData?.endpoint_keys?.has_mapping
      );
    }
  }, [endpointKeysData]);

  const resetDescendants = (descendants?: string[]) => {
    descendants?.forEach((descendant) => {
      setValue(`keys.${descendant}`, null);
    });
  };

  return !viewMode ? (
    <div className={`endpoint-keys-wrap`}>
      {!fieldsSections ? (
        <div
          className={"endpoint-keys-loader endpoint-keys-loader-first"}
          data-testid={"keys-loader"}
        >
          <SkeletonLoader
            className={"endpoint-keys-loader section-row"}
            height={"20px"}
            width={"86px"}
          />
          <SkeletonLoader
            className={"endpoint-keys-loader section-row"}
            height={"26px"}
            width={"100%"}
          />
        </div>
      ) : (
        <>
          {getSections}
          {ticketEndpointFieldsLoading &&
            !endpointKeysData?.endpoint_keys?.is_final && (
              <SkeletonLoader
                className={"endpoint-keys-loader section-row"}
                height={"26px"}
                width={"100%"}
              />
            )}
        </>
      )}
    </div>
  ) : (
    <>{getFlatSections()}</>
  );
};

export default TicketEndpointKeys;
